import { useNavigate, useSearchParams } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilState } from 'recoil';
import Honeybadger from '@honeybadger-io/js';

import { interimApi } from 'api/InterimApi';
import { getWizardInstanceById } from 'api/Wizard/Wizard';

import WIZARD_CODES from 'lib/constants/wizard/wizard_codes';
import { bootPatientIntercom } from 'lib/helpers/intercomPatient';
import usePersistedFormState from 'lib/hooks/usePersistedFormState';
import { DRAFT_KEY_ID, DRAFT_STORAGE_KEY } from 'lib/helpers/draftCrypto';
import { PracticeBrandingContextProvider } from 'lib/context/PracticeBrandingContext/PracticeBrandingContextProvider';

import { userState } from 'components/state';
import Button from 'components/shared/Button/Button';
import Loading from 'components/shared/Loading/Loading';
import ResumeDraftDialog from 'components/Patient/SelfSchedulingV2/components/ResumeDraftDialog';
import {
  DRAFT_SCHEMA_VERSION,
  DRAFT_TTL_MS
} from 'components/Patient/SelfSchedulingV2/lib/constants';

import { SelfSchedulingContext } from './SelfSchedulingContext';
import { buildPersonalInfoSeed } from './lib/buildPersonalInfoSeed';
import { runSelfSchedulingInit } from './lib/runSelfSchedulingInit';
import { useCompletedStepCodes } from './lib/useCompletedStepCodes';

const buildPracticeAppointmentData = (practice) => ({
  practice_id: practice?.id || null,
  practice_name: practice?.name || null,
  practice_timezone: practice?.timezone || null,
  practice_phone: practice?.phone || null,
  practice_instant_booking: practice?.instant_booking,
  practice_virtual_link:
    practice?.virtual_link || practice?.display_settings?.appointment?.virtual_link || null
});

const SELF_SCHEDULING_TOKEN_KEY = 'selfSchedulingToken';

// The session token must survive reload / remount / draft-resume so insurance a
// patient enters before being identified (persisted server-side keyed by this
// token) stays linkable. A per-mount crypto.randomUUID() would rotate on reload
// and orphan those pending rows. sessionStorage scopes it to this browser tab.
const readPersistedToken = () => {
  try {
    const existing = window.sessionStorage.getItem(SELF_SCHEDULING_TOKEN_KEY);
    if (existing) return existing;
    const minted = crypto.randomUUID();
    window.sessionStorage.setItem(SELF_SCHEDULING_TOKEN_KEY, minted);
    return minted;
  } catch {
    // sessionStorage unavailable (e.g. privacy mode) — fall back to in-memory.
    return crypto.randomUUID();
  }
};

const persistToken = (value) => {
  if (!value) return;
  try {
    window.sessionStorage.setItem(SELF_SCHEDULING_TOKEN_KEY, value);
  } catch {
    // sessionStorage unavailable — token stays in React state only.
  }
};

const clearPersistedToken = () => {
  try {
    window.sessionStorage.removeItem(SELF_SCHEDULING_TOKEN_KEY);
  } catch {
    // no-op
  }
};

const SelfSchedulingContextProvider = ({ children }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [practice, setPractice] = useState(null);
  const [practices, setPractices] = useState([]);
  const [needsPracticeSelection, setNeedsPracticeSelection] = useState(false);
  const [isSelectingPractice, setIsSelectingPractice] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasToken, setHasToken] = useState(!!token);
  const [schedulingData, setSchedulingData] = useState({});
  const [appointmentData, setAppointmentData] = useState({
    practice_id: null,
    practice_name: null,
    practice_timezone: null,
    practice_phone: null,
    practitioner_name: null,
    practitioner_userId: null,
    practitioner_about: null,
    service: null,
    service_id: null,
    service_name: null,
    service_length: null,
    virtual_link: null,
    practice_virtual_link: null,
    starts_at: null,
    ends_at: null,
    stepsCompleted: null,
    patientId: null
  });
  const [stepData, setStepData] = useState({});
  const [personalInfoSeed, setPersonalInfoSeed] = useState(null);
  const [wizardState, setWizardState] = useState(null);
  const [activeSubstepCode, setActiveSubstepCode] = useState(null);
  const [selfSchedulingToken, setSelfSchedulingToken] = useState(readPersistedToken);
  const { completedStepCodes, markStepCompleted, unmarkStepCompleted } = useCompletedStepCodes();
  const [paymentMode, setPaymentMode] = useState('insurance');
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [initError, setInitError] = useState(false);
  const didInit = useRef(false);

  const paymentCaptureRef = useRef({ captureCard: null, closeModal: null });
  const [paymentProcessor, setPaymentProcessor] = useState(null);

  const [sessionUser, setSessionUser] = useRecoilState(userState);
  const wasLoggedInRef = useRef(sessionUser?.logged_in);

  // Idempotent seed used by both the check_token path (init) and the
  // Recoil-userState path (in case the global app already hydrated it).
  // Pins appointmentData.patientId so Payment's saved-cards query enables on
  // first mount, prefills Personal Info, and hydrates Recoil userState so
  // LoginCard reads the real name instead of falling back to "Patient"
  // (Utility.jsx skips checkToken on /book) — all without clobbering edits
  // already made. userState.logged_in is the canonical auth flag for the flow.
  const seedFromUser = useCallback(
    (user) => {
      if (!user?.id) return;
      setAppointmentData((prev) =>
        prev.patientId === user.id ? prev : { ...prev, patientId: user.id }
      );
      const seeded = buildPersonalInfoSeed(user);
      setPersonalInfoSeed(seeded);
      setStepData((prev) => (prev.personalInfo ? prev : { ...prev, personalInfo: seeded }));
      setSessionUser((prev) =>
        prev?.id === user.id ? prev : { ...prev, ...user, authenticated: true, logged_in: true }
      );
    },
    [setSessionUser]
  );

  useEffect(() => seedFromUser(sessionUser), [sessionUser?.id, seedFromUser]);

  // Patient support chat: boot anonymously on entry, re-boot identified once
  // the session user hydrates. Kiosk devices are blocked inside the helper.
  useEffect(() => {
    bootPatientIntercom(sessionUser?.id ? { user: sessionUser } : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionUser?.id]);

  // After an explicit user-initiated login (logged_in flips false → true),
  // overwrite stepData.personalInfo with the canonical profile. seedFromUser's
  // guarded write would otherwise keep any tentative typing from the
  // unauthenticated path, but post-login we want the saved profile to win.
  useEffect(() => {
    const wasLoggedIn = wasLoggedInRef.current;
    wasLoggedInRef.current = sessionUser?.logged_in;
    if (wasLoggedIn || !sessionUser?.logged_in || !sessionUser?.id) return;
    const seeded = buildPersonalInfoSeed(sessionUser);
    setPersonalInfoSeed(seeded);
    setStepData((prev) => ({ ...prev, personalInfo: seeded }));
  }, [sessionUser]);

  // Once the booking is confirmed, drop the persisted token so a brand-new
  // booking in the same tab starts a fresh session (the just-linked insurance
  // already has its token cleared server-side).
  useEffect(() => {
    if (isConfirmed) clearPersistedToken();
  }, [isConfirmed]);

  const updateSchedulingData = useCallback((partial) => {
    setSchedulingData((prev) => ({ ...prev, ...partial }));
  }, []);

  const updateAppointmentData = useCallback((partial) => {
    setAppointmentData((prev) => ({ ...prev, ...partial }));
  }, []);

  const updateStepData = useCallback((stepKey, data) => {
    setStepData((prev) => ({ ...prev, [stepKey]: data }));
  }, []);

  // Bind the draft to an identity so a second user on a shared device can't
  // resume the first user's wizard. Prefer the authenticated user id; fall
  // back to the magic-link URL token so an anonymous patient can still resume
  // their own draft when reopening the link. The per-mount selfSchedulingToken
  // is deliberately NOT used here — it regenerates on reload and would defeat
  // the binding.
  const ownerId = sessionUser?.id ?? token ?? null;

  const draftPayload = useMemo(
    // completedStepCodes is a Set tracked client-side (the server step status is
    // not refetched mid-flow), so it must be serialized as an array and restored
    // on resume — otherwise a resumed draft lands on a restricted substep at 0%.
    () => ({
      stepData,
      appointmentData,
      schedulingData,
      activeSubstepCode,
      completedStepCodes: [...completedStepCodes],
      selfSchedulingToken
    }),
    [
      stepData,
      appointmentData,
      schedulingData,
      activeSubstepCode,
      completedStepCodes,
      selfSchedulingToken
    ]
  );

  // A brand-new visit auto-seeds appointmentData (practice info + patientId) and
  // stepData.personalInfo (from the profile), so persisting whenever the payload
  // is merely non-empty would surface the "resume draft" dialog for a wizard the
  // user never actually started. Only treat the draft as resumable once there is
  // real user progress — a completed step or a chosen time slot. Until then we
  // pass data: null so usePersistedFormState skips the save entirely.
  const hasMeaningfulProgress = useMemo(
    () =>
      completedStepCodes.size > 0 ||
      appointmentData.starts_at != null ||
      stepData.providerDateTime != null,
    [completedStepCodes, appointmentData.starts_at, stepData.providerDateTime]
  );

  const {
    hasDraft,
    isHydrated: isDraftHydrated,
    resume: resumeDraft,
    discard: discardDraft
  } = usePersistedFormState({
    storageKey: DRAFT_STORAGE_KEY,
    keyId: DRAFT_KEY_ID,
    data: hasMeaningfulProgress ? draftPayload : null,
    ttlMs: DRAFT_TTL_MS,
    schemaVersion: DRAFT_SCHEMA_VERSION,
    ownerId,
    // Stop persistence permanently once the appointment is booked so the
    // Confirmation discard can't be re-armed by a trailing save.
    enabled: !isConfirmed
  });

  const resumeFromDraft = useCallback(() => {
    const snapshot = resumeDraft();
    if (!snapshot) return;
    // Merge each slice so seeded fields (e.g. patientId from seedFromUser,
    // personalInfo prefill) are preserved if they aren't in the snapshot.
    if (snapshot.stepData) setStepData((prev) => ({ ...prev, ...snapshot.stepData }));
    if (snapshot.appointmentData) {
      // Drop patientId from the snapshot before merge so a foreign patientId
      // can never be reintroduced into the current session — useCards et al
      // would otherwise query with another user's id.
      const { patientId: _ignored, ...restAppointment } = snapshot.appointmentData;
      setAppointmentData((prev) => ({ ...prev, ...restAppointment }));
    }
    if (snapshot.schedulingData) {
      setSchedulingData((prev) => ({ ...prev, ...snapshot.schedulingData }));
    }
    if (snapshot.activeSubstepCode) setActiveSubstepCode(snapshot.activeSubstepCode);
    // Rehydrate client-side step completion so progress + step gating survive a
    // resume (persisted as an array above; markStepCompleted dedupes).
    if (Array.isArray(snapshot.completedStepCodes)) {
      snapshot.completedStepCodes.forEach(markStepCompleted);
    }
    // Restore the session token so insurance staged under it (held server-side,
    // keyed by token) re-links to this session after a resume.
    if (snapshot.selfSchedulingToken) {
      setSelfSchedulingToken(snapshot.selfSchedulingToken);
      persistToken(snapshot.selfSchedulingToken);
    }
    // Re-seed identity from the authenticated user (if any) so patientId is
    // restored to the current session, not the snapshot owner.
    if (sessionUser?.id) {
      setAppointmentData((prev) =>
        prev.patientId === sessionUser.id ? prev : { ...prev, patientId: sessionUser.id }
      );
    }
  }, [resumeDraft, sessionUser?.id, markStepCompleted]);

  // "Start over": discard the draft AND truly reset the session. discardDraft
  // alone only wipes the encrypted localStorage blob — it leaves the in-memory
  // wizard state and, crucially, the selfSchedulingToken untouched, so insurance
  // a patient staged before identifying themselves (held server-side keyed by
  // that token and registered in WizardInstance.data) is re-fetched and the
  // cards survive. Minting a fresh token in place would desync it from
  // WizardInstance.data and break the (token, patientId) read authorization
  // after Personal Info. Instead we drop the token and reload the current URL:
  // readPersistedToken() mints a new one, runSelfSchedulingInit re-creates the
  // wizard instance from scratch, and the reload preserves the magic-link
  // ?token= entry context. localStorage.removeItem inside discard() runs
  // synchronously before its awaits, so the blob is gone even if the reload
  // races the IDB key cleanup.
  const startOverDraft = useCallback(async () => {
    await discardDraft();
    clearPersistedToken();
    window.location.reload();
  }, [discardDraft]);

  const clearDraftPersistence = useCallback(async () => {
    await discardDraft();
    clearPersistedToken();
  }, [discardDraft]);

  // Sets the appointmentBook cookie + fetches the wizard instance for the
  // chosen practice. Shared between the auto-select path (token / single link)
  // and the manual selectPractice path.
  const initializeWizardForPractice = useCallback(
    async (resolvedPractice) => {
      await interimApi(
        '/api/patient/appointment/self_schedule',
        { practice_id: resolvedPractice.id },
        navigate
      );

      const wizardResponse = await getWizardInstanceById(navigate, {
        practice_id: resolvedPractice.id,
        wizard_kind_code: WIZARD_CODES.SELF_SCHEDULING
      });

      if (wizardResponse?.code !== 0 || !wizardResponse?.data?.instance) {
        Honeybadger.notify(
          `SelfScheduling init: unexpected wizard response code=${wizardResponse?.code}`
        );
        setInitError(true);
        return false;
      }

      setPractice(resolvedPractice);
      setAppointmentData((prev) => ({
        ...prev,
        ...buildPracticeAppointmentData(resolvedPractice)
      }));
      setWizardState(wizardResponse.data);
      return true;
    },
    [navigate]
  );

  const selectPractice = useCallback(
    async (chosenPractice) => {
      if (!chosenPractice?.id) return false;
      setIsSelectingPractice(true);
      try {
        const ok = await initializeWizardForPractice(chosenPractice);
        if (ok) setNeedsPracticeSelection(false);
        return ok;
      } catch (error) {
        Honeybadger.notify(`SelfScheduling selectPractice error: ${error}`);
        setInitError(true);
        return false;
      } finally {
        setIsSelectingPractice(false);
      }
    },
    [initializeWizardForPractice]
  );

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    setIsLoading(true);
    runSelfSchedulingInit({
      token,
      navigate,
      hasToken,
      setHasToken,
      seedFromUser,
      initializeWizardForPractice,
      setPractices,
      setNeedsPracticeSelection,
      setInitError
    }).finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (isLoading || !isDraftHydrated) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (!hasToken && !sessionUser?.logged_in) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <p>Please log in to view this page</p>
        <Button onClick={() => navigate('/login')} text="Go to login" />
      </div>
    );
  }

  if (initError) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <p>Something went wrong while loading the scheduling wizard. Please try again.</p>
        <Button onClick={() => window.location.reload()} text="Retry" />
      </div>
    );
  }

  return (
    <SelfSchedulingContext.Provider
      value={{
        practice,
        practices,
        needsPracticeSelection,
        selectPractice,
        isSelectingPractice,
        token,
        schedulingData,
        updateSchedulingData,
        appointmentData,
        updateAppointmentData,
        stepData,
        updateStepData,
        personalInfoSeed,
        wizardState,
        selfSchedulingToken,
        activeSubstepCode,
        setActiveSubstepCode,
        hasDraft,
        resumeFromDraft,
        discardDraft,
        startOverDraft,
        clearDraftPersistence,
        completedStepCodes,
        markStepCompleted,
        unmarkStepCompleted,
        paymentMode,
        setPaymentMode,
        isConfirmed,
        setIsConfirmed,
        paymentCaptureRef,
        paymentProcessor,
        setPaymentProcessor
      }}>
      <PracticeBrandingContextProvider branding={practice?.branding}>
        {hasDraft ? (
          <div className="h-screen bg-gradient-to-b from-primary-50 to-primary-200">
            <ResumeDraftDialog />
          </div>
        ) : (
          children
        )}
      </PracticeBrandingContextProvider>
    </SelfSchedulingContext.Provider>
  );
};

export default SelfSchedulingContextProvider;
